import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo, forwardRef } from 'react';
import { CanvasNodeDragContext, type CanvasNodeDrag } from './canvasNodeDrag';
import { connectionMidpoint, connectionPath, connectorPoint } from './canvasConnections';
import { gridPaint, gridShift } from './canvasGrid';
import { ViewportTransform, Connection, CanvasNode } from '../types';
import { Plus, Minus, MousePointer2, X, Maximize, Map as MapIcon } from 'lucide-react';
import { animatePanToNode } from '../utils/canvasUtils';
import { isFarZoom, setFarZoom } from '../utils/farZoom';
import { cardZoomStep, setCardZoom } from '../utils/cardZoom';
import { CanvasMinimap } from './CanvasMinimap';
import { LOADING_SCREEN_CLOSED } from './GlassOrb';
import {
  MIN_SCALE, fitBounds, nodeRect, nodesBounds, overlaps, unionBounds, viewBounds, zoomedAtCenter,
  type Bounds, type Insets,
} from '../utils/canvasView';
import { hasZoomModifier, zoomModifierLabel } from '../utils/platform';
import { isLocalMediaUrl } from '../utils/desktopFolder';

interface StorageFileDrop {
  name: string;
  url: string;
  type: 'image' | 'video' | 'file';
  fullPath: string;
}

interface InfiniteCanvasProps {
  children: React.ReactNode;
  transform: ViewportTransform;
  onTransformChange: (transform: ViewportTransform) => void;
  connections: Connection[];
  nodes: CanvasNode[];
  pendingConnection: { sourceId: string; mousePos: { x: number; y: number } } | null;
  onConnectionDelete?: (connectionId: string) => void;
  onTextDrop?: (text: string, position: { x: number; y: number }) => void;
  onStorageFileDrop?: (file: StorageFileDrop, position: { x: number; y: number }) => void;
  /** A click on empty canvas (no drag). */
  onBackgroundClick?: () => void;
  /** Shift-drag on empty canvas drew a box: the nodes it touches. additive: Ctrl or Cmd was held too. */
  onMarqueeSelect?: (nodeIds: string[], additive: boolean) => void;
  /** Shown on the map. */
  selectedNodeIds?: string[];
  /** The nodes that move when `id` is dragged (the selection, a script's outputs); just `id` when unset. */
  getDragGroup?: (id: string) => string[];
  /** Another page lies over the canvas: its controls (zoom rail, map, hints) are not drawn. */
  covered?: boolean;
}

/** Whether the map is shown is remembered in this browser. */
const MAP_HIDDEN_KEY = 'noodle.canvas.mapHidden';
const readMapHidden = (): boolean => {
  try { return localStorage.getItem(MAP_HIDDEN_KEY) === '1'; } catch { return false; }
};
const writeMapHidden = (hidden: boolean) => {
  try { if (hidden) localStorage.setItem(MAP_HIDDEN_KEY, '1'); else localStorage.removeItem(MAP_HIDDEN_KEY); } catch { /* private window */ }
};
/** What the connections are drawn in (SVG strokes): ink, flame under the pointer, lavender while one is being pulled. */
// The lines between cards are drawn in the theme's own colours (currentColor, through a class), so they turn with the dark theme
const GLIDE_MS = 380;
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/** Where the zoom controls stand: 16px from the edge, and left of a drawer open on the right (the app sets --canvas-right-inset on the page, in px, while one is). */
const ZOOM_RAIL_RIGHT = 'calc(16px + var(--canvas-right-inset, 0px))';
/** How much of the canvas's right side such a drawer covers right now, in px. */
const rightDrawerInset = (): number =>
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--canvas-right-inset')) || 0;

/** The margin "show all nodes" leaves around the nodes where no control asks for more. */
const SHOW_ALL_PADDING = 80;

/**
 * The view "show all nodes" goes to: every node, as large as fits and at most at 100%. Nodes
 * too far apart to fit at the smallest zoom would leave the view on the empty middle between
 * them. It then goes to the screenful that holds the most nodes (of two that hold as many, the
 * one nearer the middle of all nodes) and to what else fits beside it, so it always ends on nodes.
 * (Not exported: this file exports the canvas alone, which keeps it hot-reloadable.)
 */
const showAllView = (
  nodes: Pick<CanvasNode, 'position' | 'size'>[],
  width: number,
  height: number,
  insets: Partial<Insets> = {},
): ViewportTransform => {
  const all = nodesBounds(nodes);
  if (!all) return { x: 0, y: 0, scale: 1 };
  const fit = (bounds: Bounds) => fitBounds(bounds, width, height, { padding: SHOW_ALL_PADDING, insets });
  // How much of the canvas the free part of the screen holds at the smallest zoom
  const roomWidth = Math.max(1, width - (insets.left ?? SHOW_ALL_PADDING) - (insets.right ?? SHOW_ALL_PADDING)) / MIN_SCALE;
  const roomHeight = Math.max(1, height - (insets.top ?? SHOW_ALL_PADDING) - (insets.bottom ?? SHOW_ALL_PADDING)) / MIN_SCALE;
  const fits = (bounds: Bounds) => bounds.maxX - bounds.minX <= roomWidth && bounds.maxY - bounds.minY <= roomHeight;
  if (fits(all)) return fit(all);

  const boxes: Bounds[] = nodes.map(node => {
    const r = nodeRect(node);
    return { minX: r.x, minY: r.y, maxX: r.x + r.width, maxY: r.y + r.height };
  });
  const centres = boxes.map(box => ({ x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 }));
  const distance = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);
  // The middle of all nodes: the median of their centres, which a few far-off nodes do not move
  const median = (values: number[]) => values.slice().sort((a, b) => a - b)[Math.floor(values.length / 2)];
  const middle = { x: median(centres.map(c => c.x)), y: median(centres.map(c => c.y)) };

  // A screenful around each node in turn: the one with the most nodes wholly in it
  let group: number[] = [];
  let groupFromMiddle = Infinity;
  centres.forEach((centre, seed) => {
    const minX = centre.x - roomWidth / 2;
    const minY = centre.y - roomHeight / 2;
    const inside: number[] = [];
    boxes.forEach((box, index) => {
      if (box.minX >= minX && box.maxX <= minX + roomWidth && box.minY >= minY && box.maxY <= minY + roomHeight) inside.push(index);
    });
    // (a node larger than a screenful is at least there itself)
    if (inside.length === 0) inside.push(seed);
    const fromMiddle = distance(centre, middle);
    if (inside.length > group.length || (inside.length === group.length && fromMiddle < groupFromMiddle)) {
      group = inside;
      groupFromMiddle = fromMiddle;
    }
  });

  let bounds = group.map(index => boxes[index]).reduce((sum, box) => unionBounds(sum, box));
  // And the nodes beside it that still fit, nearest first
  const taken = new Set(group);
  const groupCentre = { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };
  const others = boxes.map((_, index) => index).filter(index => !taken.has(index))
    .sort((a, b) => distance(centres[a], groupCentre) - distance(centres[b], groupCentre));
  for (const index of others) {
    const wider = unionBounds(bounds, boxes[index]);
    if (fits(wider)) bounds = wider;
  }
  return fit(bounds);
};

/** How long the hint about pan, zoom and select stays once a workspace is in view. */
const HINT_MS = 6000;
const HINT_FADE_MS = 200;

/**
 * The hint about pan, zoom and select: a small glass pill above the page switch. It is there
 * while the canvas is empty, and for a few seconds after a workspace opens; then it goes, so it
 * never lies over nodes that are being worked on. A component of its own: its coming and going
 * renders nothing else of the canvas.
 */
const CanvasHint = React.memo(({ empty }: { empty: boolean }) => {
  // A workspace has just opened: when the canvas starts, and each time a loading screen has gone from over it
  const [justOpened, setJustOpened] = useState(true);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const opened = () => {
      setJustOpened(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setJustOpened(false), HINT_MS);
    };
    opened();
    window.addEventListener(LOADING_SCREEN_CLOSED, opened);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(LOADING_SCREEN_CLOSED, opened);
    };
  }, []);
  const shown = empty || justOpened;
  // It fades out before it is taken away
  const [present, setPresent] = useState(shown);
  useEffect(() => {
    if (shown) { setPresent(true); return; }
    const timer = setTimeout(() => setPresent(false), HINT_FADE_MS);
    return () => clearTimeout(timer);
  }, [shown]);
  if (!shown && !present) return null;
  return (
    // Above the workspace's page switch (Nodes / Edit), which is 16px from the bottom and 42px tall
    <div
      className={`absolute bottom-[68px] left-1/2 -translate-x-1/2 z-50 pointer-events-none transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}
      aria-hidden={!shown}
      data-canvas-hint
    >
      <div className="ns-glass ns-glass-strong ns-bar ns-t-meta flex items-center gap-2 px-3 py-1.5 text-ink-soft whitespace-nowrap">
        <MousePointer2 size={12} aria-hidden="true" />
        <span>Pan: scroll or drag</span>
        <span className="ns-divider-v my-0.5" aria-hidden="true" />
        <span>Zoom: {zoomModifierLabel()}+scroll or pinch</span>
        <span className="ns-divider-v my-0.5" aria-hidden="true" />
        <span>Select: Shift+drag</span>
      </div>
    </div>
  );
});
CanvasHint.displayName = 'CanvasHint';

/** Nodes being dragged: moved on screen, with the lines to them, until the app has their new places. */
interface LiveDrag {
  ids: Set<string>;
  elements: HTMLElement[];
  /** Each element's own will-change, put back when the drag is over. */
  willChange: string[];
  /** The lines to and from the dragged nodes: only these are redrawn. */
  lines: { connection: Connection; path: SVGPathElement }[];
  /** How far the nodes are from where they started, in canvas units. */
  dx: number;
  dy: number;
  /** The animation frame that will draw the latest move, if one is waiting. */
  frame: number | null;
  /** Dropped: the offsets stay until the nodes' new places arrive. */
  ended: boolean;
}

export interface InfiniteCanvasHandle {
  containerRef: React.RefObject<HTMLDivElement>;
  contentLayerRef: React.RefObject<HTMLDivElement>;
  panToNode: (nodeX: number, nodeY: number, nodeWidth: number, nodeHeight: number) => void;
  /** The view as it is right now, also in the middle of a pan or zoom (React state only has it once the gesture ends). */
  getLiveView: () => ViewportTransform;
}

export const InfiniteCanvas = forwardRef<InfiniteCanvasHandle, InfiniteCanvasProps>(({
  children,
  transform,
  onTransformChange,
  connections,
  nodes,
  pendingConnection,
  onConnectionDelete,
  onTextDrop,
  onStorageFileDrop,
  onBackgroundClick,
  onMarqueeSelect,
  selectedNodeIds,
  getDragGroup,
  covered = false,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentLayerRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);
  const [hoveredConnectionId, setHoveredConnectionId] = useState<string | null>(null);
  // Shift-drag selection box, in the container's pixels
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const marqueeRef = useRef<{ pointerId: number; additive: boolean; box: { x0: number; y0: number; x1: number; y1: number } } | null>(null);
  // A press on empty canvas, to tell a click (clears the selection) from a pan
  const pressRef = useRef<{ pointerId: number; x: number; y: number; moved: boolean } | null>(null);
  
  // Animation cancel function ref
  const cancelAnimation = useRef<(() => void) | null>(null);

  // Who wants to know of every change of view, also mid-gesture (the map)
  const viewListeners = useRef(new Set<(t: ViewportTransform) => void>());
  const notifyView = useCallback((t: ViewportTransform) => {
    viewListeners.current.forEach(listener => listener(t));
  }, []);
  const subscribeView = useCallback((listener: (t: ViewportTransform) => void) => {
    viewListeners.current.add(listener);
    return () => { viewListeners.current.delete(listener); };
  }, []);
  
  // Multi-touch gesture support
  const activePointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const lastTwoFingerCenter = useRef<{ x: number; y: number } | null>(null);
  const initialPinchDistance = useRef<number | null>(null);
  
  // Touch gesture tracking (for two-finger swipe + pinch on touch devices)
  const lastTouchCenter = useRef<{ x: number; y: number } | null>(null);
  const lastTouchDistance = useRef<number | null>(null);
  
  // PERFORMANCE: Live transform ref for direct DOM manipulation during gestures
  // This bypasses React's reconciliation entirely during zoom/pan
  const liveTransform = useRef<ViewportTransform>({ ...transform });
  const isGesturing = useRef(false);
  const rafId = useRef<number | null>(null);
  
  // Sync live transform when prop changes (e.g., from reset or external updates)
  useEffect(() => {
    if (!isGesturing.current) {
      liveTransform.current = { ...transform };
    }
  }, [transform]);
  
  const getLiveView = useCallback(() => ({ ...liveTransform.current }), []);
  useEffect(() => { notifyView(transform); }, [transform, notifyView]);

  // The dot grid is a layer of its own (canvasGrid.ts), painted for one zoom and shifted with
  // the view. It is kept from here, not by React, so it is always what was last put there.
  const gridRef = useRef<HTMLDivElement>(null);
  const gridScale = useRef<number | null>(null);
  /** Shifts the dots to the view; they are painted again for another zoom only when `repaint`. */
  const placeGrid = useCallback((t: ViewportTransform, repaint: boolean) => {
    const grid = gridRef.current;
    if (!grid) return;
    if (t.scale !== gridScale.current) {
      // In the middle of a zoom the dots wait: painting a layer the size of the canvas on
      // every frame is what made zooming slow
      if (!repaint) return;
      Object.assign(grid.style, gridPaint(t.scale));
      gridScale.current = t.scale;
    }
    grid.style.transform = gridShift(t, window.devicePixelRatio || 1);
  }, []);
  useLayoutEffect(() => { placeGrid(transform, true); }, [transform, placeGrid]);

  // Zoomed out far, the cards show a light stand-in for their bodies (utils/farZoom.ts). The
  // mark is set on the layer as the zoom crosses the line, in the middle of a gesture too, so
  // the cards switch before the browser draws them all at the new size
  const farRef = useRef(false);
  const markFar = useCallback((scale: number) => {
    const far = isFarZoom(scale, farRef.current);
    if (far === farRef.current) return;
    farRef.current = far;
    contentLayerRef.current?.toggleAttribute('data-far-zoom', far);
    setFarZoom(far);
  }, []);
  useLayoutEffect(() => { markFar(transform.scale); }, [transform.scale, markFar]);
  useEffect(() => () => setFarZoom(false), []);

  // Zoomed out, the cards' insides are zoomed up in steps so they stay readable (utils/cardZoom.ts,
  // index.css "Cards read from a distance"). The step goes on the layer as the zoom crosses a
  // line, in the middle of a gesture too
  const cardZoomRef = useRef(1);
  const markCardZoom = useCallback((scale: number) => {
    const step = cardZoomStep(scale, cardZoomRef.current);
    if (step === cardZoomRef.current) return;
    cardZoomRef.current = step;
    const layer = contentLayerRef.current;
    if (layer) {
      layer.style.setProperty('--card-zoom', String(step));
      // Marked while zoomed up: what no longer fits a card scrolls (index.css)
      layer.toggleAttribute('data-card-zoomed', step > 1);
    }
    setCardZoom(step);
  }, []);
  useLayoutEffect(() => { markCardZoom(transform.scale); }, [transform.scale, markCardZoom]);
  useEffect(() => () => setCardZoom(1), []);

  // Apply transform directly to DOM without React re-render
  const applyTransformToDOM = useCallback(() => {
    const { x, y, scale } = liveTransform.current;
    if (contentLayerRef.current) {
      // translate3d forces GPU compositing — much faster than translate + scale
      contentLayerRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    }
    markFar(scale);
    markCardZoom(scale);
    placeGrid(liveTransform.current, false);
    notifyView(liveTransform.current);
  }, [notifyView, placeGrid, markFar, markCardZoom]);

  // Schedule a DOM update via requestAnimationFrame
  const scheduleUpdate = useCallback(() => {
    if (rafId.current === null) {
      rafId.current = requestAnimationFrame(() => {
        applyTransformToDOM();
        rafId.current = null;
      });
    }
  }, [applyTransformToDOM]);
  
  // Commit the live transform to React state (call when gesture ends)
  const commitTransform = useCallback(() => {
    isGesturing.current = false;
    onTransformChange({ ...liveTransform.current });
  }, [onTransformChange]);
  
  // Start a gesture; it takes over from any glide under way
  const startGesture = useCallback(() => {
    isGesturing.current = true;
    if (cancelAnimation.current) {
      cancelAnimation.current();
      cancelAnimation.current = null;
    }
  }, []);
  
  // Cleanup pending animation frames
  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, []);

  // Debounce timer for committing transform after wheel stops
  const wheelCommitTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Handle Wheel events:
  // - Two-finger swipe (no modifier) = ZOOM
  // - Shift + two-finger swipe = PAN
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      // Check if the wheel event originated from inside a scrollable element within a node
      const target = e.target as HTMLElement;
      
      // For nodes with scroll-trap (StorageNode, StoryboardNode), completely block canvas zoom/pan
      // so the user can scroll freely without accidentally zooming the canvas
      const scrollTrap = target.closest('.node-scroll-trap');
      if (scrollTrap) {
        // Find the scrollable area inside the node and manually scroll it
        const scrollable = scrollTrap.querySelector('.node-scrollable-content') as HTMLElement;
        if (scrollable) {
          scrollable.scrollTop += e.deltaY;
        }
        e.preventDefault();
        e.stopPropagation();
        return; // Completely suppress canvas zoom/pan for these nodes
      }
      
      const scrollableParent = target.closest('.node-scrollable-content');
      
      if (scrollableParent) {
        const el = scrollableParent as HTMLElement;
        const isScrollable = el.scrollHeight > el.clientHeight;
        const isAtTop = el.scrollTop === 0;
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        
        const scrollingUp = e.deltaY < 0;
        const scrollingDown = e.deltaY > 0;
        
        // If scrollable and not at boundary, let the element handle the scroll
        if (isScrollable && !((isAtTop && scrollingUp) || (isAtBottom && scrollingDown))) {
          return; // Don't handle this event, let the element scroll
        }
      }
      
      e.preventDefault();
      if (!containerRef.current) return;

      // Start gesture mode for GPU acceleration
      if (!isGesturing.current) {
        startGesture();
      }
      
      // Clear any pending commit
      if (wheelCommitTimeout.current) {
        clearTimeout(wheelCommitTimeout.current);
      }

      if (hasZoomModifier(e)) {
        // CTRL + SCROLL (or CMD on Apple platforms) = ZOOM; trackpad pinch also arrives as ctrlKey.
        // Pinch deltas are small and fractional and need a strong factor; a mouse wheel notch
        // is 100 or more and would jump the zoom by half at that factor.
        const isWheelNotch = e.deltaMode !== 0 || Math.abs(e.deltaY) >= 50;
        const zoomSensitivity = e.ctrlKey && !e.metaKey && !isWheelNotch ? 0.01 : 0.002;
        const zoomFactor = Math.exp(-e.deltaY * zoomSensitivity);
        const newScale = Math.min(Math.max(0.1, liveTransform.current.scale * zoomFactor), 5);

        const rect = containerRef.current.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;

        const newX = cursorX - (cursorX - liveTransform.current.x) * (newScale / liveTransform.current.scale);
        const newY = cursorY - (cursorY - liveTransform.current.y) * (newScale / liveTransform.current.scale);

        liveTransform.current = { x: newX, y: newY, scale: newScale };
      } else {
        // NO MODIFIER = PAN (two-finger swipe on trackpad)
        liveTransform.current.x -= e.deltaX;
        liveTransform.current.y -= e.deltaY;
      }
      
      // Update DOM directly without React
      scheduleUpdate();
      
      // Commit to React state after wheel stops (250ms debounce)
      wheelCommitTimeout.current = setTimeout(() => {
        commitTransform();
      }, 250);
    },
    [startGesture, scheduleUpdate, commitTransform]
  );

  // Attach non-passive wheel listener for preventing default browser zoom
  // and touch listeners for two-finger swipe panning
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    
    // Handle touch start - track initial finger positions and distance
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        // Skip canvas gestures if inside a scroll-trap node (StorageNode, StoryboardNode)
        const target = e.touches[0].target as HTMLElement;
        if (target.closest('.node-scroll-trap')) return;
        
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        lastTouchCenter.current = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2,
        };
        // Track distance between fingers for pinch-to-zoom
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        lastTouchDistance.current = Math.sqrt(dx * dx + dy * dy);
      }
    };
    
    // Handle touch move - simultaneous pan + pinch-to-zoom (Figma-style)
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && lastTouchCenter.current) {
        // Skip canvas gestures if inside a scroll-trap node (StorageNode, StoryboardNode)
        const target = e.touches[0].target as HTMLElement;
        if (target.closest('.node-scroll-trap')) return;
        
        e.preventDefault();
        
        if (!isGesturing.current) {
          startGesture();
        }
        
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const newCenter = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2,
        };
        
        // Pinch-to-zoom: compute new distance between fingers
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        const newDistance = Math.sqrt(dx * dx + dy * dy);
        
        if (lastTouchDistance.current && lastTouchDistance.current > 0) {
          const scaleFactor = newDistance / lastTouchDistance.current;
          const newScale = Math.min(Math.max(0.1, liveTransform.current.scale * scaleFactor), 5);
          
          // Zoom toward the center point between fingers
          const rect = containerRef.current!.getBoundingClientRect();
          const focalX = newCenter.x - rect.left;
          const focalY = newCenter.y - rect.top;
          
          const newX = focalX - (focalX - liveTransform.current.x) * (newScale / liveTransform.current.scale);
          const newY = focalY - (focalY - liveTransform.current.y) * (newScale / liveTransform.current.scale);
          
          liveTransform.current = { x: newX, y: newY, scale: newScale };
        }
        
        lastTouchDistance.current = newDistance;
        
        // Pan: apply movement delta on top of zoom
        const deltaX = newCenter.x - lastTouchCenter.current.x;
        const deltaY = newCenter.y - lastTouchCenter.current.y;
        lastTouchCenter.current = newCenter;
        
        liveTransform.current.x += deltaX;
        liveTransform.current.y += deltaY;
        scheduleUpdate();
      }
    };
    
    // Handle touch end - reset tracking and commit transform
    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        lastTouchCenter.current = null;
        lastTouchDistance.current = null;
        if (isGesturing.current) {
          commitTransform();
        }
      }
    };
    
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    // Handle keyboard arrow keys for panning
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input
      if ((e.target as HTMLElement).tagName === 'INPUT' || 
          (e.target as HTMLElement).tagName === 'TEXTAREA' ||
          (e.target as HTMLElement).isContentEditable) {
        return;
      }
      
      // Shift+1: every node in view (as in other canvas tools)
      if (e.shiftKey && e.code === 'Digit1' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        showAllRef.current();
        return;
      }

      const PAN_AMOUNT = 50; // Pixels to pan per key press
      let handled = false;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          liveTransform.current.x += PAN_AMOUNT;
          handled = true;
          break;
        case 'ArrowRight':
          e.preventDefault();
          liveTransform.current.x -= PAN_AMOUNT;
          handled = true;
          break;
        case 'ArrowUp':
          e.preventDefault();
          liveTransform.current.y += PAN_AMOUNT;
          handled = true;
          break;
        case 'ArrowDown':
          e.preventDefault();
          liveTransform.current.y -= PAN_AMOUNT;
          handled = true;
          break;
      }
      
      if (handled) {
        applyTransformToDOM();
        // Commit immediately for keyboard (discrete actions)
        onTransformChange({ ...liveTransform.current });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleWheel, startGesture, scheduleUpdate, commitTransform, applyTransformToDOM, onTransformChange]);

  // A finger or a pen does not hover. The card it lands on is marked, and gets what a card under
  // the mouse has: boxes that scroll and its hover controls (index.css, "Canvas node cards")
  const touchedCard = useRef<Element | null>(null);
  const markTouchedCard = (e: React.PointerEvent) => {
    const card = e.pointerType === 'mouse' ? null : (e.target as Element).closest('.canvas-node');
    if (card === touchedCard.current) return;
    touchedCard.current?.removeAttribute('data-touched');
    card?.setAttribute('data-touched', '');
    touchedCard.current = card;
  };

  // Handle Panning with Two-Finger Gesture Support
  const handlePointerDown = (e: React.PointerEvent) => {
    // Only pan if clicking on the background (not bubbling up from a node or connection button)
    if ((e.target as HTMLElement).closest('.canvas-node') || (e.target as HTMLElement).closest('.connection-btn')) return;

    // Prevent default touch behaviors (like browser zoom)
    e.preventDefault();

    // Shift-drag draws a selection box instead of panning
    if (e.shiftKey && onMarqueeSelect && activePointers.current.size === 0 && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const box = { x0: e.clientX - rect.left, y0: e.clientY - rect.top, x1: e.clientX - rect.left, y1: e.clientY - rect.top };
      marqueeRef.current = { pointerId: e.pointerId, additive: e.ctrlKey || e.metaKey, box };
      setMarquee(box);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      return;
    }
    pressRef.current = activePointers.current.size === 0 ? { pointerId: e.pointerId, x: e.clientX, y: e.clientY, moved: false } : null;

    // Store pointer information
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Check if this is a two-finger gesture
    if (activePointers.current.size === 2) {
      const pointers = Array.from(activePointers.current.values());
      lastTwoFingerCenter.current = {
        x: (pointers[0].x + pointers[1].x) / 2,
        y: (pointers[0].y + pointers[1].y) / 2,
      };
      
      // Calculate initial distance between fingers for pinch detection
      const dx = pointers[1].x - pointers[0].x;
      const dy = pointers[1].y - pointers[0].y;
      initialPinchDistance.current = Math.sqrt(dx * dx + dy * dy);
      
      setIsPanning(false); // Disable single-finger panning
    } else if (activePointers.current.size === 1) {
      // Single finger/mouse panning
      setIsPanning(true);
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    }

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const drawing = marqueeRef.current;
    if (drawing && drawing.pointerId === e.pointerId && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      drawing.box = { ...drawing.box, x1: e.clientX - rect.left, y1: e.clientY - rect.top };
      setMarquee(drawing.box);
      return;
    }
    const press = pressRef.current;
    if (press && press.pointerId === e.pointerId && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 4) press.moved = true;

    // Update pointer position if it's being tracked
    if (activePointers.current.has(e.pointerId)) {
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    // Handle two-finger gesture
    if (activePointers.current.size === 2 && lastTwoFingerCenter.current && initialPinchDistance.current !== null) {
      // Skip canvas gestures if inside a scroll-trap node (StorageNode, StoryboardNode)
      const target = e.target as HTMLElement;
      if (target.closest('.node-scroll-trap')) return;
      
      e.preventDefault(); // Prevent browser zoom
      
      if (!isGesturing.current) {
        startGesture();
      }
      
      const pointers = Array.from(activePointers.current.values());
      
      // Calculate new distance between fingers
      const dx = pointers[1].x - pointers[0].x;
      const dy = pointers[1].y - pointers[0].y;
      const newDistance = Math.sqrt(dx * dx + dy * dy);
      
      // Calculate distance change ratio
      const distanceChangeRatio = Math.abs(newDistance - initialPinchDistance.current) / initialPinchDistance.current;
      
      // If fingers are spreading/pinching significantly (more than 10% change), treat as pinch-to-zoom
      if (distanceChangeRatio > 0.1) {
        initialPinchDistance.current = newDistance;
        return;
      }
      
      // Two-finger pan (when fingers stay roughly same distance apart)
      const newCenter = {
        x: (pointers[0].x + pointers[1].x) / 2,
        y: (pointers[0].y + pointers[1].y) / 2,
      };

      const panDx = newCenter.x - lastTwoFingerCenter.current.x;
      const panDy = newCenter.y - lastTwoFingerCenter.current.y;

      lastTwoFingerCenter.current = newCenter;

      // Update live transform and DOM directly
      liveTransform.current.x += panDx;
      liveTransform.current.y += panDy;
      scheduleUpdate();
      return;
    }

    // Handle single-finger panning
    if (isPanning && lastMousePos.current) {
      if (!isGesturing.current) {
        startGesture();
      }
      
      const dx = e.clientX - lastMousePos.current.x;
      const dy = e.clientY - lastMousePos.current.y;

      lastMousePos.current = { x: e.clientX, y: e.clientY };

      // Update live transform and DOM directly
      liveTransform.current.x += dx;
      liveTransform.current.y += dy;
      scheduleUpdate();
    }
  };

  /** The nodes a selection box (container pixels) touches, from the current view. */
  const nodesInBox = (box: { x0: number; y0: number; x1: number; y1: number }): string[] => {
    const t = liveTransform.current;
    const left = (Math.min(box.x0, box.x1) - t.x) / t.scale;
    const right = (Math.max(box.x0, box.x1) - t.x) / t.scale;
    const top = (Math.min(box.y0, box.y1) - t.y) / t.scale;
    const bottom = (Math.max(box.y0, box.y1) - t.y) / t.scale;
    return nodes.filter(node => {
      const width = node.size?.width ?? 320;
      const height = node.size?.height ?? 200;
      return node.position.x < right && node.position.x + width > left && node.position.y < bottom && node.position.y + height > top;
    }).map(node => node.id);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const drawing = marqueeRef.current;
    if (drawing && drawing.pointerId === e.pointerId) {
      // Leaving the canvas does not end the box; letting go (or a cancel) does
      if (e.type === 'pointerleave') return;
      marqueeRef.current = null;
      setMarquee(null);
      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
      const { box } = drawing;
      if (Math.abs(box.x1 - box.x0) > 4 || Math.abs(box.y1 - box.y0) > 4) onMarqueeSelect?.(nodesInBox(box), drawing.additive);
      return;
    }
    const press = pressRef.current;
    if (press && press.pointerId === e.pointerId && e.type !== 'pointerleave') {
      pressRef.current = null;
      if (!press.moved && activePointers.current.size <= 1) onBackgroundClick?.();
    }

    // Remove pointer from tracking
    activePointers.current.delete(e.pointerId);

    // Reset states if no more pointers are active
    if (activePointers.current.size === 0) {
      setIsPanning(false);
      lastMousePos.current = null;
      lastTwoFingerCenter.current = null;
      initialPinchDistance.current = null;
      
      // Commit transform to React state when gesture ends
      if (isGesturing.current) {
        commitTransform();
      }
    } else if (activePointers.current.size === 1) {
      // If one finger is still down, switch to single-finger panning
      const remaining = Array.from(activePointers.current.values())[0];
      setIsPanning(true);
      lastMousePos.current = { x: remaining.x, y: remaining.y };
      lastTwoFingerCenter.current = null;
      initialPinchDistance.current = null;
    }

    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  // --- DRAG AND DROP HANDLERS ---
  const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault(); // Essential to allow dropping
      e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      // Calculate canvas coordinates
      const x = (e.clientX - rect.left - transform.x) / transform.scale;
      const y = (e.clientY - rect.top - transform.y) / transform.scale;

      // Check for storage file drop first
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData && onStorageFileDrop) {
          try {
              const parsed = JSON.parse(jsonData);
              if (parsed.type === 'storage-file' && parsed.file) {
                  onStorageFileDrop(parsed.file, { x, y });
                  return;
              }
          } catch (err) {
              // Not valid JSON, continue to text drop
          }
      }

      // Handle text drop. Not the address a picture of the local folder is shown from: dragging
      // such a picture gives its address as text, which holds this run's id and belongs in no node
      const text = e.dataTransfer.getData('text/plain');
      if (text && onTextDrop && !isLocalMediaUrl(text.trim())) {
          onTextDrop(text, { x, y });
      }
  };


  // Zoom controls - these are discrete actions, so commit immediately
  const zoomIn = () => {
    const currentScale = liveTransform.current.scale;
    const newScale = Math.min(5, currentScale * 1.15);
    
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const newX = centerX - (centerX - liveTransform.current.x) * (newScale / currentScale);
      const newY = centerY - (centerY - liveTransform.current.y) * (newScale / currentScale);
      liveTransform.current = { x: newX, y: newY, scale: newScale };
    } else {
      liveTransform.current.scale = newScale;
    }
    
    applyTransformToDOM();
    onTransformChange({ ...liveTransform.current });
  };

  const zoomOut = () => {
    const currentScale = liveTransform.current.scale;
    const newScale = Math.max(0.1, currentScale / 1.15);
    
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const newX = centerX - (centerX - liveTransform.current.x) * (newScale / currentScale);
      const newY = centerY - (centerY - liveTransform.current.y) * (newScale / currentScale);
      liveTransform.current = { x: newX, y: newY, scale: newScale };
    } else {
      liveTransform.current.scale = newScale;
    }
    
    applyTransformToDOM();
    onTransformChange({ ...liveTransform.current });
  };

  /** The canvas's size on screen, for fitting views and drawing the map. */
  const [viewSize, setViewSize] = useState({ width: 1200, height: 800 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setViewSize(prev => (prev.width === el.clientWidth && prev.height === el.clientHeight ? prev : { width: el.clientWidth, height: el.clientHeight }));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /** Draws the view somewhere at once (the grid too), without telling React. */
  const showView = useCallback((t: ViewportTransform) => {
    liveTransform.current = { ...t };
    placeGrid(t, true);
    applyTransformToDOM();
  }, [applyTransformToDOM, placeGrid]);

  /** Puts the view somewhere at once; `commit` hands it to React. */
  const placeView = useCallback((t: ViewportTransform, commit: boolean) => {
    showView(t);
    isGesturing.current = !commit;
    if (commit) onTransformChange({ ...t });
  }, [showView, onTransformChange]);

  /** A drag on the map: it takes over from any glide under way. */
  const moveView = useCallback((t: ViewportTransform, commit: boolean) => {
    if (cancelAnimation.current) cancelAnimation.current();
    placeView(t, commit);
  }, [placeView]);

  /** Glides the view to `target`; any gesture stops it. */
  const glideTo = useCallback((target: ViewportTransform) => {
    if (cancelAnimation.current) cancelAnimation.current();
    const from = { ...liveTransform.current };
    let frame = 0;
    let cancelled = false;
    const started = performance.now();
    const step = (now: number) => {
      if (cancelled) return;
      const p = Math.min(1, (now - started) / GLIDE_MS);
      const k = easeInOut(p);
      const t = { x: from.x + (target.x - from.x) * k, y: from.y + (target.y - from.y) * k, scale: from.scale + (target.scale - from.scale) * k };
      if (p < 1) {
        placeView(t, false);
        frame = requestAnimationFrame(step);
      } else {
        cancelAnimation.current = null;
        placeView(target, true);
      }
    };
    frame = requestAnimationFrame(step);
    cancelAnimation.current = () => { cancelled = true; cancelAnimation.current = null; cancelAnimationFrame(frame); isGesturing.current = false; };
  }, [placeView]);

  // Smoothly centres a node in the view. It is drawn here and handed to the app once, when it
  // arrives (every frame used to re-render the app). It does not count as a gesture, so the
  // first move of a real one takes over from it.
  const panToNode = useCallback((nodeX: number, nodeY: number, nodeWidth: number, nodeHeight: number) => {
    // Cancel any ongoing animation
    if (cancelAnimation.current) {
      cancelAnimation.current();
    }
    let moved = false;
    cancelAnimation.current = animatePanToNode(
      nodeX,
      nodeY,
      nodeWidth,
      nodeHeight,
      { ...liveTransform.current },
      (newTransform) => {
        moved = true;
        showView(newTransform);
      },
      () => {
        cancelAnimation.current = null;
        if (moved) placeView(liveTransform.current, true);
      }
    );
  }, [showView, placeView]);

  // Expose refs and panToNode through forwardRef handle
  React.useImperativeHandle(ref, () => ({
    containerRef,
    contentLayerRef,
    panToNode,
    getLiveView: () => ({ ...liveTransform.current }),
  }), [panToNode]);

  // Back to 100%, staying where you are
  const resetZoom = () => {
    glideTo(zoomedAtCenter(liveTransform.current, 1, viewSize.width, viewSize.height));
  };

  /**
   * Every node in view, at most at 100%, clear of the zoom controls, the map and a drawer open
   * on the right. Nodes too far apart to fit all at once: as many as fit (see showAllView).
   */
  const showAll = useCallback(() => {
    const insets = { right: SHOW_ALL_PADDING + rightDrawerInset(), bottom: mapHiddenRef.current ? 120 : 190 };
    glideTo(showAllView(nodes, viewSize.width, viewSize.height, insets));
  }, [nodes, viewSize.width, viewSize.height, glideTo]);
  const showAllRef = useRef(showAll);
  showAllRef.current = showAll;

  const [mapHidden, setMapHidden] = useState(readMapHidden);
  const mapHiddenRef = useRef(mapHidden);
  mapHiddenRef.current = mapHidden;
  const toggleMap = (hidden: boolean) => { setMapHidden(hidden); writeMapHidden(hidden); };
  const selectedSet = useMemo(() => new Set(selectedNodeIds ?? []), [selectedNodeIds]);
  // When no node is on screen, offer the way back (judged once the view settles)
  const lost = useMemo(() => {
    const bounds = nodesBounds(nodes);
    if (!bounds) return false;
    const view = viewBounds(transform, viewSize.width, viewSize.height);
    return !nodes.some(node => overlaps(view, {
      minX: node.position.x, minY: node.position.y,
      maxX: node.position.x + (node.size?.width || 320), maxY: node.position.y + (node.size?.height || 200),
    }));
  }, [nodes, transform, viewSize.width, viewSize.height]);

  // ── Dragging nodes ──────────────────────────────────────────────────────────
  // While nodes are dragged they are moved here, on screen (a CSS translate on top of their
  // place), and the lines to them are redrawn, once a frame and without a render; the app
  // hears of the move once, on drop.
  const connectionsSvgRef = useRef<SVGSVGElement>(null);
  const nodeById = useMemo(() => new Map(nodes.map(node => [node.id, node])), [nodes]);
  const nodeByIdRef = useRef(nodeById);
  nodeByIdRef.current = nodeById;
  const connectionsRef = useRef(connections);
  connectionsRef.current = connections;
  const liveDragRef = useRef<LiveDrag | null>(null);
  const getDragGroupRef = useRef(getDragGroup);
  getDragGroupRef.current = getDragGroup;

  /** Draws the dragged nodes, and the lines to them, `dx, dy` from the nodes' own places. */
  const placeDrag = useCallback((drag: LiveDrag, dx: number, dy: number) => {
    const translate = dx || dy ? `${dx}px ${dy}px` : '';
    for (const el of drag.elements) el.style.translate = translate;
    const offset = { x: dx, y: dy };
    for (const { connection, path } of drag.lines) {
      const start = connectorPoint(nodeByIdRef.current.get(connection.sourceId), 'output', drag.ids.has(connection.sourceId) ? offset : undefined);
      const end = connectorPoint(nodeByIdRef.current.get(connection.targetId), 'input', drag.ids.has(connection.targetId) ? offset : undefined);
      path.setAttribute('d', connectionPath(start, end));
    }
  }, []);
  const clearLiveDrag = useCallback(() => {
    const drag = liveDragRef.current;
    if (!drag) return;
    if (drag.frame !== null) cancelAnimationFrame(drag.frame);
    // Back to what the nodes' own places give (React only rewrites a line whose nodes moved)
    placeDrag(drag, 0, 0);
    drag.elements.forEach((el, i) => { el.style.willChange = drag.willChange[i]; });
    liveDragRef.current = null;
  }, [placeDrag]);
  const nodeDrag = useMemo<CanvasNodeDrag>(() => ({
    begin: (id) => {
      clearLiveDrag();
      const ids = new Set(getDragGroupRef.current?.(id) ?? [id]);
      const elements: HTMLElement[] = [];
      const lines: LiveDrag['lines'] = [];
      const layer = contentLayerRef.current;
      if (layer) {
        for (const nodeId of ids) {
          const el = layer.querySelector<HTMLElement>(`[data-node-id="${CSS.escape(nodeId)}"]`);
          if (el) elements.push(el);
        }
        for (const connection of connectionsRef.current) {
          if (!ids.has(connection.sourceId) && !ids.has(connection.targetId)) continue;
          const path = connectionsSvgRef.current?.querySelector<SVGPathElement>(`[data-connection-line="${CSS.escape(connection.id)}"]`);
          if (path) lines.push({ connection, path });
        }
      }
      // Each node on a layer of its own while it moves, so moving it repaints nothing
      const willChange = elements.map(el => el.style.willChange);
      for (const el of elements) el.style.willChange = 'transform, translate';
      liveDragRef.current = { ids, elements, willChange, lines, dx: 0, dy: 0, frame: null, ended: false };
    },
    move: (dx, dy) => {
      const drag = liveDragRef.current;
      if (!drag || drag.ended) return;
      drag.dx = dx;
      drag.dy = dy;
      // Drawn once a frame, however often the pointer reports
      if (drag.frame === null) {
        drag.frame = requestAnimationFrame(() => {
          drag.frame = null;
          if (liveDragRef.current === drag && !drag.ended) placeDrag(drag, drag.dx, drag.dy);
        });
      }
    },
    end: (dx, dy) => {
      const drag = liveDragRef.current;
      if (!drag) return;
      if (!dx && !dy) { clearLiveDrag(); return; }
      if (drag.frame !== null) { cancelAnimationFrame(drag.frame); drag.frame = null; }
      // Onto the grid at once. The dropped positions come with the next nodes, and the offsets
      // go then, before the frame is drawn (below); should they never come (nothing could
      // move), they go anyway
      drag.dx = dx;
      drag.dy = dy;
      drag.ended = true;
      placeDrag(drag, dx, dy);
      requestAnimationFrame(() => requestAnimationFrame(() => { if (liveDragRef.current === drag) clearLiveDrag(); }));
    },
  }), [clearLiveDrag, placeDrag]);
  useLayoutEffect(() => {
    if (liveDragRef.current?.ended) clearLiveDrag();
  }, [nodes, clearLiveDrag]);
  // A render in the middle of a drag draws the lines where the nodes' own places put them
  // (and React rewrites the ones that changed): back to where the dragged nodes are
  useLayoutEffect(() => {
    const drag = liveDragRef.current;
    if (drag && !drag.ended) placeDrag(drag, drag.dx, drag.dy);
  });
  useEffect(() => () => {
    if (liveDragRef.current?.frame != null) cancelAnimationFrame(liveDragRef.current.frame);
  }, []);

  // Where each connection starts and ends, from the nodes' own places
  const connectionEnds = (conn: Connection) => ({
    start: connectorPoint(nodeById.get(conn.sourceId), 'output'),
    end: connectorPoint(nodeById.get(conn.targetId), 'input'),
  });
  const hoveredConnection = hoveredConnectionId ? connections.find(conn => conn.id === hoveredConnectionId) : undefined;

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative bg-cream-50 cursor-grab active:cursor-grabbing touch-none select-none"
      data-canvas-container="true"
      onPointerDownCapture={markTouchedCard}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        // No paint containment: like overflow:hidden it would clip the moving layer, see below
        contain: 'size layout style',
        touchAction: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        transform: 'translateZ(0)',
        // Clipped by a clip-path, not overflow:hidden: an overflow clip makes the browser repaint
        // the whole moving layer on every frame of a pan (about five times the paint work)
        clipPath: 'inset(0)',
      }}
    >
      <CanvasNodeDragContext.Provider value={nodeDrag}>
      {/* The dot grid: painted and shifted by placeGrid */}
      <div ref={gridRef} className="absolute pointer-events-none" style={{ willChange: 'transform' }} aria-hidden="true" data-canvas-grid />
      {/* Content Layer - GPU accelerated */}
      <div
        ref={contentLayerRef}
        className="absolute top-0 left-0 w-full h-full origin-top-left"
        data-content-layer="true"
        style={{
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
          backfaceVisibility: 'hidden',
          willChange: 'transform',
        }}
      >
        {/* SVG Connections Layer. On a compositor layer of its own: sharing one with the nodes
            drawn over it made every pan frame several times dearer */}
        <svg ref={connectionsSvgRef} className="absolute top-0 left-0 w-full h-full overflow-visible pointer-events-none z-0" style={{ willChange: 'transform' }}>
             {connections.map(conn => {
                 const { start, end } = connectionEnds(conn);
                 const isHovered = hoveredConnectionId === conn.id;
                 const d = connectionPath(start, end);
                 return (
                     <g key={conn.id} onMouseEnter={() => setHoveredConnectionId(conn.id)} onMouseLeave={() => setHoveredConnectionId(null)}>
                         {/* Thick transparent path for easier hover */}
                         <path
                            d={d}
                            stroke="transparent"
                            strokeWidth="20"
                            fill="none"
                            className="pointer-events-auto cursor-pointer"
                         />
                         {/* Visible Path */}
                         <path
                            d={d}
                            stroke="currentColor"
                            className={isHovered ? 'text-flame' : 'text-ink'}
                            strokeWidth={isHovered ? "2.5" : "1.5"}
                            fill="none"
                            strokeLinecap="round"
                            data-connection-line={conn.id}
                         />
                     </g>
                 );
             })}

             {pendingConnection && (() => {
                 const start = connectorPoint(nodeById.get(pendingConnection.sourceId), 'output');
                 const endX = (pendingConnection.mousePos.x - transform.x) / transform.scale;
                 const endY = (pendingConnection.mousePos.y - transform.y) / transform.scale;
                 return (
                     <path
                        d={connectionPath(start, {x: endX, y: endY})}
                        stroke="currentColor"
                        className="text-lavender"
                        strokeWidth="2"
                        strokeDasharray="6,5"
                        strokeLinecap="round"
                        fill="none"
                     />
                 );
             })()}
        </svg>

        {/* The delete button of the connection under the pointer (one button per connection,
            hidden until hovered, was one more piece to paint per connection on every frame) */}
        {hoveredConnection && (() => {
            const { start, end } = connectionEnds(hoveredConnection);
            const mid = connectionMidpoint(start, end);
            return (
                <div
                    className="connection-btn ns-icon-btn ns-icon-btn-xs ns-icon-btn-line ns-icon-btn-danger hover:border-danger/30 absolute -ml-3 -mt-3 z-10 shadow-soft"
                    style={{ left: mid.x, top: mid.y }}
                    // Still this connection's while the pointer is on the button
                    onMouseEnter={() => setHoveredConnectionId(hoveredConnection.id)}
                    onMouseLeave={() => setHoveredConnectionId(null)}
                    onClick={(e) => {
                        e.stopPropagation();
                        setHoveredConnectionId(null);
                        if (onConnectionDelete) onConnectionDelete(hoveredConnection.id);
                    }}
                    title="Disconnect"
                    role="button"
                    aria-label="Disconnect"
                >
                    <X />
                </div>
            );
        })()}

        {children}
      </div>

      {/* Shift-drag selection box */}
      {marquee && (
        <div
          className="absolute pointer-events-none border border-lavender-deep bg-lavender/15 rounded-sm z-[60]"
          style={{
            left: Math.min(marquee.x0, marquee.x1),
            top: Math.min(marquee.y0, marquee.y1),
            width: Math.abs(marquee.x1 - marquee.x0),
            height: Math.abs(marquee.y1 - marquee.y0),
          }}
          data-selection-box
        />
      )}

      {/* HUD / Controls */}
      {/* One of the canvas's glass bars, standing upright: the same round buttons as the top bar,
          in a column, 16px from the window's edge. It moves left of a drawer open on the right. */}
      {!covered && (
      <div
        className="ns-glass ns-glass-strong ns-bar absolute bottom-4 z-50 flex flex-col items-center gap-0.5 p-1 transition-[right] duration-200"
        style={{ right: ZOOM_RAIL_RIGHT }}
        data-zoom-rail
      >
        <button
          onClick={zoomIn}
          className="ns-icon-btn"
          title="Zoom in"
          aria-label="Zoom in"
        >
          <Plus />
        </button>
        <button type="button" className="ns-t-meta w-8 py-1 rounded-md text-center tabular-nums hover:text-ink" onClick={resetZoom} title="Reset zoom">
          {Math.round(transform.scale * 100)}%
        </button>
        <button
          onClick={zoomOut}
          className="ns-icon-btn"
          title="Zoom out"
          aria-label="Zoom out"
        >
          <Minus />
        </button>
        <div className="ns-divider self-stretch mx-2 my-0.5" aria-hidden="true" />
        <button
          onClick={showAll}
          className="ns-icon-btn"
          title="Show all nodes (Shift+1)"
          aria-label="Show all nodes"
          data-show-all
        >
          <Maximize />
        </button>
        {/* On while the map is shown: aria-pressed gives the look of what is on (lavender) */}
        <button
          onClick={() => toggleMap(!mapHidden)}
          className="ns-icon-btn"
          title={mapHidden ? 'Show the map' : 'Hide the map'}
          aria-label={mapHidden ? 'Show the map' : 'Hide the map'}
          aria-pressed={!mapHidden}
          data-map-toggle
        >
          <MapIcon />
        </button>
      </div>
      )}

      {!mapHidden && !covered && (
        <CanvasMinimap
          nodes={nodes}
          selectedIds={selectedSet}
          transform={transform}
          viewSize={viewSize}
          subscribe={subscribeView}
          getLive={getLiveView}
          onMove={moveView}
          onGlide={glideTo}
          onHide={() => toggleMap(true)}
        />
      )}

      {lost && !covered && (
        // In the row of the top bar, and as tall as it: 16px from the top, 42px
        <div className="ns-glass ns-glass-strong ns-bar absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 h-[42px] pl-4 pr-1.5 text-[13px] font-medium text-ink-soft whitespace-nowrap" onPointerDown={(e) => e.stopPropagation()} data-lost-hint>
          No nodes here
          <button onClick={showAll} className="ns-btn ns-btn-primary ns-btn-xs ns-btn-flat">
            Show all nodes
          </button>
        </div>
      )}

      {!covered && <CanvasHint empty={nodes.length === 0} />}
      </CanvasNodeDragContext.Provider>
    </div>
  );
});

InfiniteCanvas.displayName = 'InfiniteCanvas';